import { useCallback, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useAuth } from 'src/contexts/auth.context'
import { useResource } from 'src/hooks/useResource'
import { useNotification } from 'src/contexts/notification.context'
import Table from 'src/components/table/table'
import Filters from 'src/components/table/filters'
import {
  deleteResourceRequest,
  getResourcesRequest,
  getClassroomsRequest,
  getStudentsListRequest,
  assignPracticeToClassroomRequest,
  assignPracticeToStudentRequest,
  getChildrenRequest,
} from 'src/services/api.service'
import { routes } from 'src/navigation/routes'
import PracticeRow from './components/practice-row'
import AssignModal from './modals/assign-modal'

const statusOptions = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
]

export const TeacherPractices = () => {
  const history = useHistory()
  const { user, cache, setCache } = useAuth()
  const { resourceType, resourceName } = useResource()
  const { showConfirm, showNotificationHelper, showErrorHelper } =
    useNotification()

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(
    cache[resourceType]?.data || {
      items: [],
      count: 0,
      pageCount: 1,
    },
  )
  const [filter, setFilter] = useState(
    cache[resourceType]?.filter || {
      period: null,
      subject_id: null,
      grade_id: null,
      status: null,
      query: '',
    },
  )
  const [lastFetchParams, setLastFetchParams] = useState({})

  const [selectedId, setSelectedId] = useState(null)

  const [allStudents, setAllStudents] = useState([])
  const [allClassrooms, setAllClassrooms] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)

  const role = user.role_id === 3 ? 'parent' : 'teacher'

  const [isShowAssignModal, setIsShowAssignModal] = useState(false)
  const [tabAssignModal, setTabAssignModal] = useState(0)

  useEffect(() => {
    const fetchAsync = async () => {
      let response =
        user.role_id === 3
          ? await getChildrenRequest()
          : await getStudentsListRequest({ page: 1, perPage: 1000 })

      setAllStudents(response.data?.items || [])
      response = await getClassroomsRequest({ page: 1, perPage: 1000 })
      setAllClassrooms(response.data?.items || [])
    }
    fetchAsync()
  }, [])

  const fetchData = useCallback(async ({ pageSize, pageIndex, filterData }) => {
    try {
      setLastFetchParams({ pageSize, pageIndex, filterData })
      setLoading(true)

      const response = await getResourcesRequest(
        resourceType,
        {
          ...filterData,
          page: pageIndex + 1,
          perPage: pageSize,
        },
        role,
      )

      const newData = {
        items: response.data.items,
        count: response.data.total,
        pageCount: Math.ceil(response.data.total / pageSize),
      }
      setData(newData)
      if (pageIndex === 0) {
        setCache({ [resourceType]: { data: newData, filter: filterData } })
      }
    } catch (error) {
      showErrorHelper(error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Row handlers
  const handleRowClick = (id) => () => {
    setSelectedId(selectedId === id ? null : id)
  }

  const onEdit = (item) => {
    history.push(`/lessons/${resourceType}/${item.id}/edit`)
  }

  const onDelete = async (item) => {
    showConfirm({
      title: 'Are you sure?',
      body: `Do you really want to delete the ${item.title}?`,
      actions: {
        onOk: async () => {
          try {
            await deleteResourceRequest(resourceType, item.id)
            showNotificationHelper({
              title: 'Success',
              body: 'Practice has been deleted',
            })
            fetchData(lastFetchParams)
          } catch (error) {
            showErrorHelper(error)
          }
        },
      },
    })
  }

  const onAssignStudents = (item) => () => {
    setTabAssignModal(0)
    setIsShowAssignModal(true)
    setSelectedItem(item)
  }

  const onAssignClassrooms = (item) => () => {
    setTabAssignModal(1)
    setIsShowAssignModal(true)
    setSelectedItem(item)
  }

  const handleCloseModals = () => {
    setIsShowAssignModal(false)
  }

  const onSendToMark = (item) => {
    console.log(item)
  }

  const handleAssignStudentsSubmit = ({ ids, startDate, finishDate }) => {
    console.log({ ids })
    ids.map(async (id) => {
      console.log(id)
      const result = await assignPracticeToStudentRequest(
        id,
        {
          task_id: selectedItem.id,
          started_at: startDate,
          finished_at: finishDate,
        },
        role,
      )
    })
    fetchData(lastFetchParams)
    setIsShowAssignModal(false)
  }

  const handleAssignClassroomsSubmit = ({ ids, startDate, finishDate }) => {
    console.log({ ids })
    ids.map(async (id) => {
      const result = await assignPracticeToClassroomRequest(id, {
        task_id: selectedItem.id,
        started_at: startDate,
        finished_at: finishDate,
      })
    })
    fetchData(lastFetchParams)
    setIsShowAssignModal(false)
  }

  return (
    <>
      <h2 className="sectionHb-title">All {resourceName}s</h2>

      <div className="sectionHb-block purchaseHb">
        <div className="sectionHb-block__wrap">
          <Filters
            buttonComponent={
              <Link
                to={routes[resourceType].options}
                className="primary-btn primary-btn__big"
              >
                <i className="hb-ico plus-ico-white" />
                {resourceName}
              </Link>
            }
            filter={filter}
            setFilter={setFilter}
            status={{
              options: statusOptions,
            }}
          />

          <Table
            // curPage={page}
            // perPage={perPage}
            data={data.items}
            fetchData={fetchData}
            pageCount={data.pageCount}
            filter={filter}
            loading={loading}
            rowComponent={(item) => (
              <PracticeRow
                role={role}
                key={item.id}
                item={item}
                onClick={handleRowClick(item.id)}
                active={selectedId === item.id}
                actions={{
                  onEdit,
                  onDelete,
                  onAssignStudents,
                  onAssignClassrooms,
                  onSendToMark,
                }}
              />
            )}
          />
        </div>
      </div>

      <AssignModal
        tab={tabAssignModal}
        resourceName={resourceName}
        active={isShowAssignModal}
        students={allStudents}
        classrooms={allClassrooms}
        onClose={handleCloseModals}
        onSubmitStudents={handleAssignStudentsSubmit}
        onSubmitClassrooms={handleAssignClassroomsSubmit}
      />
    </>
  )
}
